import { isApiErrorBody, isApiSuccess } from "@thyroaid/shared";
import { ApiError, clientMessage } from "./errors";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type QueryValue = string | number | boolean | undefined;

/**
 * Validates and narrows the envelope's `data`. Required on every call: the envelope guard
 * checks the wrapper, not the payload, and an unchecked cast is how a clinical screen ends
 * up rendering `undefined` as a value. Throw to reject.
 */
export type Parser<T> = (data: unknown) => T;

export interface RequestOptions<T> {
  parse: Parser<T>;
  query?: Readonly<Record<string, QueryValue>>;
  body?: unknown;
  signal?: AbortSignal;
  /** Makes a retried write safe: the server applies a given key at most once (API side: B01.04+). */
  idempotencyKey?: string;
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Supplies the Firebase ID token (B01.07). Return null when signed out. */
  getAuthToken?: () => Promise<string | null>;
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
  newRequestId?: () => string;
}

export interface UploadFileOptions<T> {
  /** A real `File` from an `<input type="file">` — its own `.type`/`.name` become the request's
   * Content-Type and `X-Filename` header (B05.02: imaging upload is the first binary request
   * this client has ever needed to make; the body is the file's raw bytes, never JSON). */
  file: File;
  parse: Parser<T>;
  query?: Readonly<Record<string, QueryValue>>;
  signal?: AbortSignal;
}

export interface ApiClient {
  request<T>(method: HttpMethod, path: string, options: RequestOptions<T>): Promise<T>;
  get<T>(path: string, options: Omit<RequestOptions<T>, "body" | "idempotencyKey">): Promise<T>;
  post<T>(path: string, options: RequestOptions<T>): Promise<T>;
  put<T>(path: string, options: RequestOptions<T>): Promise<T>;
  patch<T>(path: string, options: RequestOptions<T>): Promise<T>;
  delete<T>(path: string, options: RequestOptions<T>): Promise<T>;
  /** Uploads a file's raw bytes (not JSON); the response is still the usual JSON envelope. */
  uploadFile<T>(path: string, options: UploadFileOptions<T>): Promise<T>;
  /** Downloads a raw binary response (not a JSON envelope) as a `Blob` — e.g. an imaging file. */
  downloadBlob(
    path: string,
    options?: { query?: Readonly<Record<string, QueryValue>>; signal?: AbortSignal },
  ): Promise<Blob>;
  /**
   * Notifies when the server rejects a request that carried a bearer token with UNAUTHENTICATED
   * (AUTH-008 resync: the browser believed it was signed in, e.g. the session was revoked
   * server-side). Never fires for a request made while already signed out — that is the expected
   * outcome of calling a protected endpoint with no token, not a resync signal. Returns an
   * unsubscribe function.
   */
  onUnauthenticated(listener: () => void): () => void;
}

const DEFAULT_TIMEOUT_MS = 15_000;
/** Unique abort reason, so a timeout is told apart from a caller cancellation by identity. */
const TIMEOUT_REASON = Symbol("thyroaid.api.timeout");

/** Only relative API paths: "/patients/123". Blocks absolute/protocol-relative URLs and "..". */
export function assertSafePath(path: string): void {
  const ok =
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !/(^|\/)\.\.?(\/|$)/.test(path) &&
    !/[?#\\]/.test(path);
  if (!ok)
    throw new TypeError(
      `Unsafe API path "${path}": use a relative path like "/patients/123"; pass query via options.query`,
    );
}

function buildUrl(baseUrl: string, path: string, query: RequestOptions<unknown>["query"]): string {
  assertSafePath(path);
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) params.append(key, String(value));
  }
  const qs = params.toString();
  return `${baseUrl}${path}${qs ? `?${qs}` : ""}`;
}

function isJson(response: Response): boolean {
  return /^application\/(?:[\w.+-]+\+)?json\b/i.test(response.headers.get("content-type") ?? "");
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const {
    baseUrl,
    getAuthToken,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    fetchImpl = (...args: Parameters<typeof fetch>) => fetch(...args),
    newRequestId = () => crypto.randomUUID(),
  } = options;

  const unauthenticatedListeners = new Set<() => void>();

  /**
   * Everything a request needs regardless of what its body/response actually are: token lookup,
   * the combined timeout+caller abort signal, and mapping a failed `fetch` itself (never an HTTP
   * error status, which callers handle themselves from the `Response`) to an `ApiError`.
   */
  async function fetchWithAuth(
    method: HttpMethod,
    path: string,
    opts: {
      query?: RequestOptions<unknown>["query"];
      signal?: AbortSignal;
      accept?: string;
      contentType?: string;
      extraHeaders?: Readonly<Record<string, string>>;
      body?: BodyInit;
    },
  ): Promise<{ response: Response; requestId: string; token: string | null }> {
    const url = buildUrl(baseUrl, path, opts.query);
    // Already cancelled (e.g. unmounted before the call): don't start a request at all.
    opts.signal?.throwIfAborted();
    const requestId = newRequestId();

    let token: string | null = null;
    if (getAuthToken) {
      try {
        token = await getAuthToken();
      } catch (cause) {
        // Never fall back to an unauthenticated request when the token lookup itself failed.
        throw new ApiError({
          code: "UNAUTHENTICATED",
          message: "Your session couldn't be confirmed. Sign in again.",
          requestId,
          cause,
        });
      }
    }

    const headers = new Headers({
      Accept: opts.accept ?? "application/json",
      "X-Request-Id": requestId,
    });
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (opts.contentType) headers.set("Content-Type", opts.contentType);
    for (const [name, value] of Object.entries(opts.extraHeaders ?? {})) headers.set(name, value);

    // Combine the caller's signal with our timeout; the abort reason records which one fired.
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort(TIMEOUT_REASON);
    }, timeoutMs);
    const onCallerAbort = () => {
      controller.abort(opts.signal?.reason);
    };
    opts.signal?.addEventListener("abort", onCallerAbort, { once: true });

    try {
      const response = await fetchImpl(url, {
        method,
        headers,
        // Bearer tokens only; no cookies means no CSRF surface.
        credentials: "omit",
        signal: controller.signal,
        ...(opts.body !== undefined ? { body: opts.body } : {}),
      });
      return { response, requestId, token };
    } catch (cause) {
      if (controller.signal.reason === TIMEOUT_REASON)
        throw new ApiError({
          code: "TIMEOUT",
          message: clientMessage("TIMEOUT"),
          requestId,
          cause,
        });
      // Caller cancelled (e.g. component unmounted): propagate as-is, it is not an API failure.
      if (opts.signal?.aborted) throw cause;
      throw new ApiError({
        code: "NETWORK_ERROR",
        message: clientMessage("NETWORK_ERROR"),
        requestId,
        cause,
      });
    } finally {
      clearTimeout(timer);
      opts.signal?.removeEventListener("abort", onCallerAbort);
    }
  }

  /** Throws the same `ApiError` shape `request`/`uploadFile` always have, from a non-2xx JSON
   * envelope (or a non-JSON/unparsable failure response). Fires `onUnauthenticated` exactly like
   * `request` always has. */
  async function throwForErrorResponse(
    response: Response,
    requestId: string,
    token: string | null,
  ): Promise<never> {
    const { status } = response;
    let body: unknown;
    if (isJson(response)) {
      try {
        body = await response.json();
      } catch {
        // fall through to the generic HTTP_ERROR below
      }
    }
    if (isApiErrorBody(body)) {
      // Only a token we actually sent being rejected means "the browser thinks it's signed in
      // but the server disagrees" — a request with no token failing the same way is routine.
      if (body.error.code === "UNAUTHENTICATED" && token) {
        for (const listener of unauthenticatedListeners) listener();
      }
      throw new ApiError({
        code: body.error.code,
        message: body.error.message,
        status,
        requestId: body.meta.requestId,
        issues: body.error.issues,
      });
    }
    throw new ApiError({
      code: "HTTP_ERROR",
      message: clientMessage("HTTP_ERROR"),
      status,
      requestId,
    });
  }

  async function request<T>(method: HttpMethod, path: string, opts: RequestOptions<T>): Promise<T> {
    const hasBody = opts.body !== undefined && method !== "GET";
    const extraHeaders: Record<string, string> = {};
    if (opts.idempotencyKey) extraHeaders["Idempotency-Key"] = opts.idempotencyKey;
    const { response, requestId, token } = await fetchWithAuth(method, path, {
      query: opts.query,
      signal: opts.signal,
      extraHeaders,
      ...(hasBody ? { contentType: "application/json", body: JSON.stringify(opts.body) } : {}),
    });

    const { status } = response;
    if (status === 204) return opts.parse(null);
    if (!response.ok) await throwForErrorResponse(response, requestId, token);

    let body: unknown;
    if (isJson(response)) {
      try {
        body = await response.json();
      } catch (cause) {
        throw new ApiError({
          code: "INVALID_RESPONSE",
          message: clientMessage("INVALID_RESPONSE"),
          status,
          requestId,
          cause,
        });
      }
    }

    if (!isApiSuccess(body)) {
      throw new ApiError({
        code: "INVALID_RESPONSE",
        message: clientMessage("INVALID_RESPONSE"),
        status,
        requestId,
      });
    }

    try {
      return opts.parse(body.data);
    } catch (cause) {
      throw new ApiError({
        code: "INVALID_RESPONSE",
        message: clientMessage("INVALID_RESPONSE"),
        status,
        requestId: body.meta.requestId,
        cause,
      });
    }
  }

  async function uploadFile<T>(path: string, opts: UploadFileOptions<T>): Promise<T> {
    const { response, requestId, token } = await fetchWithAuth("POST", path, {
      query: opts.query,
      signal: opts.signal,
      contentType: opts.file.type || "application/octet-stream",
      extraHeaders: opts.file.name ? { "X-Filename": opts.file.name } : {},
      body: opts.file,
    });

    const { status } = response;
    if (!response.ok) await throwForErrorResponse(response, requestId, token);

    let body: unknown;
    try {
      body = await response.json();
    } catch (cause) {
      throw new ApiError({
        code: "INVALID_RESPONSE",
        message: clientMessage("INVALID_RESPONSE"),
        status,
        requestId,
        cause,
      });
    }
    if (!isApiSuccess(body)) {
      throw new ApiError({
        code: "INVALID_RESPONSE",
        message: clientMessage("INVALID_RESPONSE"),
        status,
        requestId,
      });
    }
    try {
      return opts.parse(body.data);
    } catch (cause) {
      throw new ApiError({
        code: "INVALID_RESPONSE",
        message: clientMessage("INVALID_RESPONSE"),
        status,
        requestId: body.meta.requestId,
        cause,
      });
    }
  }

  async function downloadBlob(
    path: string,
    opts: { query?: RequestOptions<unknown>["query"]; signal?: AbortSignal } = {},
  ): Promise<Blob> {
    const { response, requestId, token } = await fetchWithAuth("GET", path, {
      query: opts.query,
      signal: opts.signal,
      accept: "*/*",
    });
    if (!response.ok) await throwForErrorResponse(response, requestId, token);
    try {
      return await response.blob();
    } catch (cause) {
      throw new ApiError({
        code: "INVALID_RESPONSE",
        message: clientMessage("INVALID_RESPONSE"),
        status: response.status,
        requestId,
        cause,
      });
    }
  }

  return {
    request,
    get: (path, o) => request("GET", path, o),
    post: (path, o) => request("POST", path, o),
    put: (path, o) => request("PUT", path, o),
    patch: (path, o) => request("PATCH", path, o),
    delete: (path, o) => request("DELETE", path, o),
    uploadFile,
    downloadBlob,
    onUnauthenticated(listener) {
      unauthenticatedListeners.add(listener);
      return () => {
        unauthenticatedListeners.delete(listener);
      };
    },
  };
}
